import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard } from "@project/admin/components/module-ui";
import { AdmissionInquiryDetailDialog } from "@project/admin/components/admission-inquiry-detail-dialog";
import { AdmissionInquiryForm } from "@project/components/admission-inquiry-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Button } from "@project/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@project/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { TablePagination } from "@project/admin/components/table-pagination";
import {
  Search,
  Eye,
  ClipboardList,
  XCircle,
  Plus,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { formatDate } from "@project/lib/date-format";
import { exportApiRowsToCsv } from "@project/lib/csv";
import { CsvExportButton } from "@project/admin/components/csv-export-button";
import { admissionInquiryService } from "@project/services/admin/admission-inquiry.service";
import { fetchAllListData } from "@project/services/admin/list-api-helpers";
import type { ApiPagination } from "@project/types/location";
import {
  formatInquiryNumber,
  type AdmissionInquiryRecord,
  type AdmissionInquiryStats,
} from "@project/types/admission-inquiry";

const PAGE_SIZE = 20;

const EMPTY_STATS: AdmissionInquiryStats = {
  total: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
};

function InquiryActions({
  row,
  onView,
  onEdit,
  onDelete,
}: {
  row: AdmissionInquiryRecord;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const inqNo = formatInquiryNumber(row);
  const iconClass = "h-8 w-8 shrink-0";

  return (
    <div className="grid w-28 grid-cols-3 items-center gap-0.5">
      <div className="flex justify-center">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={`${iconClass} text-primary`}
          onClick={onView}
          title="Open"
          aria-label={`View ${inqNo}`}
        >
          <Eye className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex justify-center">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={iconClass}
          onClick={onEdit}
          title="Edit"
          aria-label={`Edit ${inqNo}`}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex justify-center">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={`${iconClass} text-rose-600 hover:bg-rose-500/10 hover:text-rose-700`}
          onClick={onDelete}
          title="Delete"
          aria-label={`Delete ${inqNo}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function AdmissionInquiriesList() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [rows, setRows] = useState<AdmissionInquiryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<ApiPagination>({
    count: 0,
    current_page: 1,
    total_pages: 1,
    next: null,
    previous: null,
  });
  const [stats, setStats] = useState<AdmissionInquiryStats>(EMPTY_STATS);
  const [viewTarget, setViewTarget] = useState<AdmissionInquiryRecord | null>(
    null
  );
  const [addOpen, setAddOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<AdmissionInquiryRecord | null>(
    null
  );
  const [editLoading, setEditLoading] = useState(false);
  const [editFormKey, setEditFormKey] = useState(0);
  const [deleteTarget, setDeleteTarget] =
    useState<AdmissionInquiryRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  const openAddModal = () => {
    setFormKey(key => key + 1);
    setAddOpen(true);
  };

  const openEditModal = async (row: AdmissionInquiryRecord) => {
    setEditOpen(true);
    setEditLoading(true);
    setEditRecord(null);
    try {
      const record = await admissionInquiryService.getInquiry(row.id);
      setEditRecord(record ?? row);
      setEditFormKey(key => key + 1);
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, "Failed to load admission inquiry.")
      );
      setEditOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    if (editLoading) {
      return;
    }
    setEditOpen(false);
    setEditRecord(null);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(1);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [q]);

  const fetchRows = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      try {
        const result = await admissionInquiryService.listInquiriesPage({
          page: targetPage,
          page_size: PAGE_SIZE,
          search: debouncedQ || undefined,
        });
        setRows(result.data ?? []);
        setPagination(result.pagination);
        setStats(result.stats ?? EMPTY_STATS);
        if (result.pagination.current_page !== targetPage) {
          setPage(result.pagination.current_page);
        }
      } catch (error) {
        projectToast.error(
          getApiErrorMessage(error, "Failed to load admission inquiries.")
        );
      } finally {
        setLoading(false);
      }
    },
    [debouncedQ]
  );

  useEffect(() => {
    void fetchRows(page);
  }, [page, fetchRows]);

  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const exportRows = await fetchAllListData(
        query => admissionInquiryService.listInquiriesPage(query),
        { search: debouncedQ || undefined }
      );
      if (!exportRows.length) {
        projectToast.error("Nothing to export");
        return;
      }
      exportApiRowsToCsv(
        `admission-inquiries-${new Date().toISOString().slice(0, 10)}`,
        exportRows as Record<string, unknown>[]
      );
      projectToast.success(`Exported ${exportRows.length} inquiries`);
    } catch (error) {
      projectToast.error(getApiErrorMessage(error, "Failed to export CSV"));
    } finally {
      setExportingCsv(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleting(true);
    try {
      await admissionInquiryService.remove(deleteTarget.id);
      projectToast.success(
        `${formatInquiryNumber(deleteTarget)} deleted successfully.`
      );
      setDeleteTarget(null);
      const nextPage = rows.length === 1 && page > 1 ? page - 1 : page;
      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        void fetchRows(page);
      }
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, "Could not delete inquiry.")
      );
    } finally {
      setDeleting(false);
    }
  };

  const emptyMessage = useMemo(() => {
    if (debouncedQ) {
      return "No inquiries match your search.";
    }
    return "No admission inquiries found.";
  }, [debouncedQ]);

  return (
    <AdminShell
      title="Admission Inquiries"
      subtitle="Walk-in / phone admission inquiries and records"
    >
      <AdmissionInquiryDetailDialog
        open={!!viewTarget}
        onOpenChange={open => {
          if (!open) {
            setViewTarget(null);
          }
        }}
        inquiryId={viewTarget?.id ?? null}
        preview={viewTarget}
      />

      <div className="mb-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Inquiries"
          value={stats.total}
          icon={<ClipboardList className="h-5 w-5" strokeWidth={2.25} />}
          accent="from-sky-600 to-sky-500"
        />
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl overflow-y-auto rounded-2xl bg-background p-4 pb-6 sm:p-6 sm:pb-8">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              New Admission Inquiry
            </DialogTitle>
          </DialogHeader>
          {addOpen && (
            <AdmissionInquiryForm
              key={formKey}
              formId="admission-inquiry-form"
              mode="admin"
              onSubmitted={() => {
                setAddOpen(false);
                void fetchRows(1);
                setPage(1);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={editOpen}
        onOpenChange={open => {
          if (!open) {
            closeEditModal();
          }
        }}
      >
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl overflow-y-auto rounded-2xl bg-background p-4 pb-6 sm:p-6 sm:pb-8">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Edit Admission Inquiry
              {editRecord && (
                <span className="mt-1 block font-mono text-xs font-normal text-muted-foreground">
                  {formatInquiryNumber(editRecord)}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {editLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            editRecord && (
              <AdmissionInquiryForm
                key={editFormKey}
                formId="admission-inquiry-edit-form"
                mode="admin"
                editingId={editRecord.id}
                initialRecord={editRecord}
                onSubmitted={() => {
                  closeEditModal();
                  void fetchRows(page);
                }}
              />
            )
          )}
        </DialogContent>
      </Dialog>

      <div className="mb-6 mt-10 flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by inquiry no, elder or inquirer…"
            className="pl-8"
          />
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <CsvExportButton onExport={exportCsv} exporting={exportingCsv} />
          <Button
            type="button"
            size="sm"
            className="gap-1 rounded-xl"
            onClick={openAddModal}
          >
            <Plus className="h-4 w-4" /> Add Inquiry
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>
              <TableHead>Inquiry Number</TableHead>
              <TableHead>Inquirer Name</TableHead>
              <TableHead>Elder Name</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="w-28">
                <div className="flex justify-end">
                  <span className="block w-28 text-right text-xs font-medium text-muted-foreground">
                    Actions
                  </span>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-10 text-center text-sm text-muted-foreground"
                >
                  <XCircle className="text-muted-foreground/60 mx-auto mb-2 h-5 w-5" />
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-xs">
                    {formatInquiryNumber(row)}
                  </TableCell>
                  <TableCell className="font-medium">
                    {row.inquirer_name}
                  </TableCell>
                  <TableCell>{row.elder_name}</TableCell>
                  <TableCell>
                    {row.application_date
                      ? formatDate(row.application_date)
                      : row.created_at
                        ? formatDate(row.created_at)
                        : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <InquiryActions
                        row={row}
                        onView={() => setViewTarget(row)}
                        onEdit={() => void openEditModal(row)}
                        onDelete={() => setDeleteTarget(row)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        className="mt-4"
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={pagination.count}
        totalPages={pagination.total_pages}
        loading={loading}
        onPageChange={setPage}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={open => {
          if (!open && !deleting) {
            setDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent className="max-w-md rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete inquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the admission inquiry. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deleteTarget && (
            <div className="bg-muted/40 rounded-xl border border-border px-4 py-3 text-sm">
              <p className="font-mono text-xs text-muted-foreground">
                {formatInquiryNumber(deleteTarget)}
              </p>
              <p className="mt-1 font-medium text-foreground">
                {deleteTarget.elder_name}
              </p>
              <p className="text-muted-foreground">
                Inquirer: {deleteTarget.inquirer_name}
              </p>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="rounded-xl">
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              disabled={deleting}
              className="hover:bg-destructive/90 rounded-xl bg-destructive text-destructive-foreground"
              onClick={() => void confirmDelete()}
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
